import { useState, useRef } from "react";

import api from "@helpers/api";

import CustomModal from "@components/CustomModal";

import ReorderPanel from "../_shared/ReorderPanel";

// API responses are sometimes the entity directly, sometimes wrapped in { data }.
function unwrap(res) {
    const body = res?.data;
    return body?.data ?? body;
}

/**
 * Drag-to-reorder the playlists inside a section.
 *
 * Open with the section id; the modal fetches the section's playlists and
 * persists the new order via PATCH /section/{sectionId}/playlist/order.
 *
 *   modalRef.current.open({ sectionId }, onSaved)
 */
export default function ReorderPlaylistsModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [items, setItems] = useState([]);
    const [errorTitle, setErrorTitle] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const REF_sectionId = useRef(null);
    const REF_callback = useRef();
    const REF_initialOrder = useRef([]);

    modalRef.current = {
        open(config, callback) {
            REF_sectionId.current = config.sectionId ?? config.section ?? config.id;
            REF_callback.current = callback;
            setItems([]);
            setErrorTitle(null);
            setErrorMessage(null);
            setOpen(true);
            load();
        },
    };

    async function load() {
        setFetching(true);
        try {
            const section = unwrap(await api.get(`section/${REF_sectionId.current}`));
            // The section's content array mixes playlists and single lessons;
            // only true playlists (non-single) are reorderable here.
            const playlists = (section?.content ?? [])
                .filter((entry) => entry && !entry.single)
                .map((playlist) => ({
                    id: playlist.id,
                    name: playlist.name,
                    banner: playlist.banner,
                }));

            setItems(playlists);
            REF_initialOrder.current = playlists.map((playlist) => playlist.id);
        } catch (e) {
            console.error("Failed to load playlists for reorder:", e);
            setErrorTitle("Erro ao carregar");
            setErrorMessage("Não foi possível carregar as playlists. Tente novamente.");
        }
        setFetching(false);
    }

    async function handleSubmit() {
        try {
            setSaving(true);
            const playlists = items.map((item, index) => ({
                id: item.id,
                order: index,
            }));

            await api.patch(`section/${REF_sectionId.current}/playlist/order`, {
                playlists,
            });

            await REF_callback.current?.();
            setOpen(false);
        } catch (e) {
            console.error("Failed to reorder playlists:", e);
            setErrorTitle("Erro ao salvar");
            setErrorMessage("Não foi possível salvar a nova ordem. Tente novamente.");
        }
        setSaving(false);
    }

    const isDirty = items.some(
        (item, index) => item.id !== REF_initialOrder.current[index]
    );

    return (
        <CustomModal
            maxWidth={460}
            title="Reordenar playlists"
            bottomButtonSumbitText="Salvar ordem"
            bottomButtonCancelText="Cancelar"
            loading={saving || fetching}
            disabled={!isDirty || items.length === 0}
            open={open}
            handleClose={() => setOpen(false)}
            handleSubmit={handleSubmit}
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            bodyComponent={
                open ? (
                    <ReorderPanel
                        subtitle="Arraste e solte um ítem para definir a ordem das playlists nesta seção."
                        loading={fetching}
                        items={items}
                        onReorder={setItems}
                        emptyLabel="Esta seção ainda não possui playlists para reordenar."
                    />
                ) : null
            }
        />
    );
}
